import React from 'react';
import _ from 'lodash';

import { Box } from '@nerdwallet/currency/components/Box';

import lazy, { Hydrate } from '../../../lazy/lazy-universal';
import type { Browser } from '../../../../types';
import Breakdown from '../../../breakdown/breakdown';
import type { Data } from '../types';

import Schedule from './schedule';
import { debouncedTrackInputEvent } from '../../../../lib/analytics/trackers';
import useAnalytics from '../../../../lib/analytics/use-analytics';

import styles from './ac-breakdown.module.less';

// Lazy load components (Tab Content)
const Amortization = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-amortization' */ '../../../amortization-chart/amortization-chart'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const CompareLoanTypes = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-compare-loan-types' */ '../../../compare-loan-types'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const SECTION_NAMES = {
  AMORTIZATION_SCHEDULE: 'Amortization schedule',
  BREAKDOWN: 'Breakdown',
  COMPARE_LOAN_TYPES: 'Compare loan types',
};

const TABS_NAME = [
  SECTION_NAMES.AMORTIZATION_SCHEDULE,
  SECTION_NAMES.BREAKDOWN,
  SECTION_NAMES.COMPARE_LOAN_TYPES,
];

interface ACBreakdownProps {
  browser: Browser;
  data?: Data;
}

const ACBreakdown: React.FC<ACBreakdownProps> = ({ data, browser }) => {
  const {
    inputs: { loanTerm },
    outputs: { schedule, halfway, monthlySchedule },
  } = data;
  const segmentAbstract = useAnalytics();

  const trackSectionChange = (idx) => {
    // segment analytics
    debouncedTrackInputEvent(
      {
        entityName: 'form_field_section',
        field: _.startCase(_.toLower(TABS_NAME[idx])), // Title case
        field_type: 'tab_switch',
        field_value: 'tab switch',
      },
      segmentAbstract
    );
  };

  const trackYearChange = (year) => {
    // segment analytics
    debouncedTrackInputEvent(
      {
        field_type: 'slide',
        entityName: 'slide',
        field: 'Amortization',
        field_value: year,
      },
      segmentAbstract
    );
  };

  const content = [
    {
      label: SECTION_NAMES.AMORTIZATION_SCHEDULE,
      content: (
        <Amortization
          loanTerm={loanTerm}
          schedule={schedule}
          halfway={halfway}
          onYearChange={trackYearChange}
          hideInsight
          calculatorNameForSegment="mortgage_amortization_calculator"
        />
      ),
    },
    {
      label: SECTION_NAMES.BREAKDOWN,
      content: <Schedule browser={browser} monthlySchedule={monthlySchedule} />,
    },
    {
      label: SECTION_NAMES.COMPARE_LOAN_TYPES,
      content: <CompareLoanTypes />,
    },
  ];

  return (
    <Box>
      <Breakdown
        className={styles['ac-breakdown']}
        content={content}
        onSectionChange={trackSectionChange}
      />
    </Box>
  );
};

export default ACBreakdown;
